import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { useDocumentTitle } from '@/lib/useDocumentTitle';
import type { ReactNode } from 'react';

interface AuthLayoutProps {
  title: string;
  description?: string;
  /** A failure that belongs to the whole form rather than one field. */
  error?: string | null;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * The screen's area for the testing team's tests: the page is
   * `<testId>-page`, and its title, description and error get `-title`,
   * `-description` and `-error`. See docs/test-ids.md.
   */
  testId: string;
}

/** The centred card the four sign-in screens share. */
export function AuthLayout({
  title,
  description,
  error,
  children,
  footer,
  testId,
}: AuthLayoutProps) {
  useDocumentTitle(title);
  return (
    <Container
      component="main"
      id="main"
      maxWidth="xs"
      data-testid={`${testId}-page`}
      sx={{ py: 8 }}
    >
      <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 600 }}>
        ActivistARC
      </Typography>

      <Paper variant="outlined" sx={{ mt: 3, p: 4 }}>
        <Typography
          variant="h6"
          component="h2"
          data-testid={`${testId}-title`}
          sx={{ fontWeight: 600 }}
        >
          {title}
        </Typography>

        {description ? (
          <Typography
            color="text.secondary"
            variant="body2"
            data-testid={`${testId}-description`}
            sx={{ mt: 0.5 }}
          >
            {description}
          </Typography>
        ) : null}

        {error ? (
          // role="alert" so a screen reader announces it. Showing it silently
          // would leave somebody submitting again and hearing nothing.
          <Alert
            severity="error"
            role="alert"
            data-testid={`${testId}-error`}
            sx={{ mt: 2 }}
          >
            {error}
          </Alert>
        ) : null}

        <Box sx={{ mt: 3 }}>{children}</Box>
      </Paper>

      {footer ? <Box sx={{ mt: 2, textAlign: 'center' }}>{footer}</Box> : null}
    </Container>
  );
}
